import React, { useEffect, useState } from 'react';
import { useTranslations } from 'hooks/useTranslations';
import { useLocation, useNavigate } from 'react-router-dom';
import { Resolver, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import useAmmunitionFieldsFormSchema from 'pages/AncillariesPage/AncillariesReferenceTablePage/ARTAmmunitionForm/schemaValidation';
import { useLang } from 'models/langContext';
import { useAncillaries } from 'pages/AncillariesPage/hooks';
import { EDictionaryTypes } from 'models/dictionary';
import { EArtAmmunitionType } from 'constants/ArtAccessoryType';
import { getFieldsByAmmunitionType } from 'pages/AncillariesPage/helpers';
import Grid from '@mui/material/Grid';
import { SearchWrapper } from 'components/atoms/SearchWrapper';
import { Typography } from '@mui/material';
import { mapDictionaryToOptionProp } from 'helpers/dictionary';
import Button from 'components/atoms/Button';
import { EButtonVariants } from 'constants/Buttons';
import { FormInput } from 'components/molecules/FormInput';
import { useScroll } from 'hooks/useScroll';
import { FormWyswigEditor } from 'components/molecules/FormWyswigEditor';
import { FormSearchSelect } from 'components/molecules/FormSearchSelect';
import { FormAsyncAutocomplete } from 'components/molecules/FormAsyncAutocomplete';

export enum ESearchFormLabels {
  ammunitionTypeUniqueId = 'ancillaryReferenceTable.addAncillary.ammunition.ammunitionType.label',
  manufacturerUniqueId = 'ancillaryReferenceTable.addAncillary.ammunition.ammunitionManufacturer.label',
  productName = 'ancillaryReferenceTable.addAncillary.ammunition.productName.label',
  factoryUniqueId = 'ancillaryReferenceTable.addAncillary.ammunition.factory.label',
  calibreUniqueId = 'ancillaryReferenceTable.addAncillary.ammunition.calibre.label',
  cartridgeCompositionUniqueId = 'ancillaryReferenceTable.addAncillary.ammunition.cartridgeComposition.label',
  colourOfPrimerSealantUniqueId = 'ancillaryReferenceTable.addAncillary.ammunition.colourOfPrimeSealant.label',
  projectileShapeUniqueId = 'ancillaryReferenceTable.addAncillary.ammunition.projectileShape.label',
  functionalTypeUniqueId = 'ancillaryReferenceTable.addAncillary.ammunition.functionalType.label',
  munitionTypeUniqueId = 'ancillaryReferenceTable.addAncillary.ammunition.munitionType.label',
  shotSizeUniqueId = 'ancillaryReferenceTable.addAncillary.ammunition.shotSize.label',
  munitionVelocityUniqueId = 'ancillaryReferenceTable.addAncillary.ammunition.munitionVelocity.label',
  alphanumericalMarkings = 'ancillaryReferenceTable.addAncillary.ammunition.alphanumericalMarkings.label',
  notes = 'ancillaryReferenceTable.addAncillary.ammunition.notes.label',
}

export interface IAmmunitionFormValues {
  ammunitionTypeUniqueId: string;
  manufacturerUniqueId: string;
  productName: string;
  factoryUniqueId: string;
  calibreUniqueId: string;
  cartridgeCompositionUniqueId: string;
  colourOfPrimerSealantUniqueId: string;
  projectileShapeUniqueId: string;
  functionalTypeUniqueId: string;
  munitionTypeUniqueId: string;
  shotSizeUniqueId: string;
  munitionVelocityUniqueId: string;
  alphanumericalMarkings: string;
  notes?: string;
  invokedByPageChange?: boolean;
}

export const initialFormValues: IAmmunitionFormValues = {
  ammunitionTypeUniqueId: '',
  manufacturerUniqueId: '',
  productName: '',
  factoryUniqueId: '',
  calibreUniqueId: '',
  cartridgeCompositionUniqueId: '',
  colourOfPrimerSealantUniqueId: '',
  projectileShapeUniqueId: '',
  functionalTypeUniqueId: '',
  munitionTypeUniqueId: '',
  shotSizeUniqueId: '',
  munitionVelocityUniqueId: '',
  alphanumericalMarkings: '',
  notes: '',
};

interface IARTAmmunitionForm {
  onSubmit: (arg: IAmmunitionFormValues) => void;
  onCancel: () => void;
  initialData?: IAmmunitionFormValues;
  isEdit?: boolean;
}

const ARTAmmunitionForm = ({
  onSubmit,
  onCancel,
  initialData,
  isEdit = false,
}: IARTAmmunitionForm) => {
  const { t } = useTranslations();
  const [accessFields, setAccessFields] = useState<string[]>([]);
  const navigate = useNavigate();
  const { state: routerState }: any = useLocation();
  const { schema } = useAmmunitionFieldsFormSchema();
  const { elRef, scrollToError } = useScroll();
  const [isSubmitLoading, setSubmitLoading] = useState(false);
  const {
    control,
    handleSubmit,
    reset,
    setValue,
    setError,
    clearErrors,
    getValues,
    formState: { errors },
  } = useForm<IAmmunitionFormValues>({
    defaultValues: isEdit ? initialData : initialFormValues,
    resolver: yupResolver(schema) as unknown as Resolver<IAmmunitionFormValues>,
  });

  const { selectedLanguage } = useLang();

  const {
    loadDictionaries,
    artAmmunitionType,
    artAmmunitionManufacturer,
    artAmmunitionFactory,
    artAmmunitionCalibreGauge,
    artAmmunitionCartridgeComposition,
    artAmmunitionColourOfPrimerSealant,
    artAmmunitionProjectileShape,
    artAmmunitionFunctionalType,
    artAmmunitionShotSize,
    artAmmunitionMunitionType,
    artAmmunitionMunitionVelocity,
  } = useAncillaries();

  useEffect(() => {
    (async function initDictionaries() {
      await loadDictionaries([
        EDictionaryTypes.ArtAmmunitionType,
        EDictionaryTypes.ArtAmmunitionManufacturer,
        EDictionaryTypes.ArtAmmunitionFactory,
        EDictionaryTypes.ArtAmmunitionCalibreGauge,
        EDictionaryTypes.ArtAmmunitionCartridgeComposition,
        EDictionaryTypes.ArtAmmunitionColourOfPrimerSealant,
        EDictionaryTypes.ArtAmmunitionProjectileShape,
        EDictionaryTypes.ArtAmmunitionFunctionalType,
        EDictionaryTypes.ArtAmmunitionShotSize,
        EDictionaryTypes.ArtAmmunitionMunitionType,
        EDictionaryTypes.ArtAmmunitionMunitionVelocity,
      ]);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLanguage]);

  useEffect(() => {
    reset(initialFormValues);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    (async () => {
      scrollToError(errors);
    })();
  }, [errors]);

  const submitForm = async (data: IAmmunitionFormValues) => {
    setSubmitLoading(true);
    await onSubmit(data);
    setSubmitLoading(false);
  };

  const handleCancel = () => {
    if (routerState?.prevPage) {
      navigate(routerState.prevPage);
    } else {
      onCancel();
    }
  };

  const ammunitionTypeChange = (value: EArtAmmunitionType | '') => {
    const notes = getValues('notes');
    if (isEdit) {
      reset({ ...initialData, ammunitionTypeUniqueId: value, notes });
    } else {
      reset({ ...initialFormValues, ammunitionTypeUniqueId: value, notes });
    }
    if (value) {
      const fields = getFieldsByAmmunitionType(value);
      setAccessFields(fields);
    } else {
      setAccessFields([]);
    }
  };

  useEffect(() => {
    if (isEdit && initialData?.ammunitionTypeUniqueId) {
      ammunitionTypeChange(initialData.ammunitionTypeUniqueId as EArtAmmunitionType);
      reset(initialData);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const checkDisabled = (fieldName: string) => !accessFields.includes(fieldName);

  return (
    <form onSubmit={handleSubmit(submitForm)}>
      <Grid container spacing={2} columns={10} ref={elRef}>
        <Grid size={{ xs: 10 }} style={{ marginBottom: '10px' }}>
          <SearchWrapper>
            <Typography variant="h5">
              {t('ancillariesDetails.accordion.title.label')}
            </Typography>
          </SearchWrapper>
        </Grid>
        <Grid size={{ xs: 10, sm: 5, md: 3, lg: 2.5 }}>
          <FormSearchSelect
            options={mapDictionaryToOptionProp(artAmmunitionType)}
            name="ammunitionTypeUniqueId"
            label={t(ESearchFormLabels.ammunitionTypeUniqueId)}
            setValueAdditionalBehavior={ammunitionTypeChange}
            canBeEmpty
            control={control}
            errors={errors}
            onSelect={ammunitionTypeChange}
            disabled={isEdit}
            required={true}
          />
        </Grid>
        <Grid size={{ xs: 10, sm: 5, md: 3, lg: 2.5 }}>
          <FormAsyncAutocomplete
            dictionaryType={EDictionaryTypes.ArtAmmunitionManufacturer}
            name="manufacturerUniqueId"
            label={t(ESearchFormLabels.manufacturerUniqueId)}
            control={control}
            errors={errors}
            disabled={checkDisabled('manufacturersUniqueIds')}
            required={true}
          />
        </Grid>
        <Grid size={{ xs: 10, sm: 5, md: 3, lg: 2.5 }}>
          <FormInput
            charsLimit={120}
            maxLength
            name="productName"
            label={t(ESearchFormLabels.productName)}
            control={control}
            errors={errors}
            setValue={setValue}
            disabled={checkDisabled('productName')}
          />
        </Grid>
        <Grid size={{ xs: 10, sm: 5, md: 3, lg: 2.5 }}>
          <FormSearchSelect
            options={mapDictionaryToOptionProp(artAmmunitionFactory)}
            name="factoryUniqueId"
            label={t(ESearchFormLabels.factoryUniqueId)}
            canBeEmpty
            control={control}
            errors={errors}
            disabled={checkDisabled('factoriesUniqueIds')}
            required={true}
          />
        </Grid>
        <Grid size={{ xs: 10, sm: 5, md: 3, lg: 2.5 }}>
          <FormAsyncAutocomplete
            dictionaryType={EDictionaryTypes.ArtAmmunitionCalibreGauge}
            name="calibreUniqueId"
            label={t(ESearchFormLabels.calibreUniqueId)}
            control={control}
            errors={errors}
            disabled={checkDisabled('calibresUniqueIds')}
            required={true}
          />
        </Grid>
        <Grid size={{ xs: 10, sm: 5, md: 3, lg: 2.5 }}>
          <FormSearchSelect
            options={mapDictionaryToOptionProp(artAmmunitionCartridgeComposition)}
            name="cartridgeCompositionUniqueId"
            label={t(ESearchFormLabels.cartridgeCompositionUniqueId)}
            canBeEmpty
            control={control}
            errors={errors}
            disabled={checkDisabled('cartridgeCompositionUniqueId')}
            required={true}
          />
        </Grid>
        <Grid size={{ xs: 10, sm: 5, md: 3, lg: 2.5 }}>
          <FormSearchSelect
            options={mapDictionaryToOptionProp(artAmmunitionColourOfPrimerSealant)}
            name="colourOfPrimerSealantUniqueId"
            label={t(ESearchFormLabels.colourOfPrimerSealantUniqueId)}
            canBeEmpty
            control={control}
            errors={errors}
            disabled={checkDisabled('coloursOfPrimerSealantUniqueIds')}
            required={true}
          />
        </Grid>
        <Grid size={{ xs: 10, sm: 5, md: 3, lg: 2.5 }}>
          <FormSearchSelect
            options={mapDictionaryToOptionProp(artAmmunitionProjectileShape)}
            name="projectileShapeUniqueId"
            label={t(ESearchFormLabels.projectileShapeUniqueId)}
            canBeEmpty
            control={control}
            errors={errors}
            disabled={checkDisabled('projectileShapesUniqueIds')}
            required={true}
          />
        </Grid>
        <Grid size={{ xs: 10, sm: 5, md: 3, lg: 2.5 }}>
          <FormSearchSelect
            options={mapDictionaryToOptionProp(artAmmunitionFunctionalType)}
            name="functionalTypeUniqueId"
            label={t(ESearchFormLabels.functionalTypeUniqueId)}
            canBeEmpty
            control={control}
            errors={errors}
            disabled={checkDisabled('functionalTypesUniqueIds')}
            required={true}
          />
        </Grid>
        <Grid size={{ xs: 10, sm: 5, md: 3, lg: 2.5 }}>
          <FormSearchSelect
            options={mapDictionaryToOptionProp(artAmmunitionShotSize)}
            name="shotSizeUniqueId"
            label={t(ESearchFormLabels.shotSizeUniqueId)}
            canBeEmpty
            control={control}
            errors={errors}
            disabled={checkDisabled('shotSizesUniqueIds')}
            required={true}
          />
        </Grid>
        <Grid size={{ xs: 10, sm: 5, md: 3, lg: 2.5 }}>
          <FormSearchSelect
            options={mapDictionaryToOptionProp(artAmmunitionMunitionType)}
            name="munitionTypeUniqueId"
            label={t(ESearchFormLabels.munitionTypeUniqueId)}
            canBeEmpty
            control={control}
            errors={errors}
            disabled={checkDisabled('munitionTypesUniqueIds')}
            required={true}
          />
        </Grid>
        <Grid size={{ xs: 10, sm: 5, md: 3, lg: 2.5 }}>
          <FormSearchSelect
            options={mapDictionaryToOptionProp(artAmmunitionMunitionVelocity)}
            name="munitionVelocityUniqueId"
            label={t(ESearchFormLabels.munitionVelocityUniqueId)}
            canBeEmpty
            control={control}
            errors={errors}
            disabled={checkDisabled('munitionVelocitiesUniqueIds')}
            required={true}
          />
        </Grid>
        <Grid size={{ xs: 10, sm: 5, md: 3, lg: 2.5 }}>
          <FormInput
            charsLimit={120}
            maxLength
            name="alphanumericalMarkings"
            label={t(ESearchFormLabels.alphanumericalMarkings)}
            control={control}
            errors={errors}
            setValue={setValue}
            disabled={checkDisabled('alphanumericalMarkings')}
            required={true}
          />
        </Grid>

        <Grid size={{ xs: 10 }}>
          <FormWyswigEditor
            name="notes"
            label={t(ESearchFormLabels.notes)}
            setValue={setValue}
            control={control}
            errors={errors}
            disabled={checkDisabled('notes')}
            setError={setError}
            clearErrors={clearErrors}
            charsLimit={5000}
          />
        </Grid>

        <Grid size={{ xs: 10, sm: 10, md: 6, lg: 5 }} sx={{ mt: 2 }}>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6 }} sx={{ pr: '6px' }}>
              <Button
                type="submit"
                fullWidth
                label={t('general.save.button')}
                id="save-button"
                isLoading={isSubmitLoading}
                variant={EButtonVariants.contained}
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }} sx={{ pl: '3px', pr: '6px' }}>
              <Button
                fullWidth
                label={t('general.cancel.button')}
                id="cancel-button"
                variant={EButtonVariants.outlined}
                onClick={handleCancel}
              />
            </Grid>
          </Grid>
        </Grid>

        <Grid size={{ xs: 10 }}>
          <Typography variant="caption">{t('general.mandatoryField.text')}</Typography>
        </Grid>
      </Grid>
    </form>
  );
};

export default ARTAmmunitionForm;
